// Piano UI and input handling (mouse, keyboard, MIDI)
import { COLORS } from './data';

interface KeyElement {
  el: HTMLDivElement;
  sw: HTMLDivElement;
  color: string;
  black: boolean;
}

export class PianoController {
  private keyEls: Record<number, KeyElement> = {};
  private mouseDown = false;
  private active: Set<number>;
  private onNoteOn: (midi: number, vel?: number) => void;
  private onNoteOff: (midi: number) => void;

  constructor(
    active: Set<number>,
    onNoteOn: (midi: number, vel?: number) => void,
    onNoteOff: (midi: number) => void
  ) {
    this.active = active;
    this.onNoteOn = onNoteOn;
    this.onNoteOff = onNoteOff;
    this.buildPiano();
    this.setupKeyboard();
    this.setupMIDI();
    this.setupPointerEvents();
  }

  private buildPiano() {
    const keys = document.getElementById('keys');
    if (!keys) return;

    for (let midi = 21; midi <= 108; midi++) {
      const info = COLORS[midi];
      if (!info) continue;

      const black = info[0].includes('#');
      const el = document.createElement('div');
      el.className = black ? 'bk' : 'wk';
      el.dataset.midi = String(midi);

      const sw = document.createElement('div');
      sw.className = 'swatch';

      if (black) {
        el.appendChild(sw);
      } else {
        const lab = document.createElement('div');
        lab.className = 'lab';
        lab.textContent = info[0];
        el.appendChild(lab);
        el.appendChild(sw);
      }

      el.addEventListener('pointerdown', e => {
        e.preventDefault();
        this.mouseDown = true;
        this.onNoteOn(midi);
      });
      el.addEventListener('pointerenter', () => {
        if (this.mouseDown) this.onNoteOn(midi);
      });
      el.addEventListener('pointerup', () => this.onNoteOff(midi));
      el.addEventListener('pointerleave', () => {
        if (this.mouseDown) this.onNoteOff(midi);
      });

      keys.appendChild(el);
      this.keyEls[midi] = { el, sw, color: info[1], black };
    }

    // Scroll to middle C
    const scroll = document.getElementById('pianoScroll');
    const midC = this.keyEls[60];
    if (midC && scroll) {
      scroll.scrollLeft = midC.el.offsetLeft - scroll.clientWidth / 2;
    }
  }

  paintKeys() {
    for (const midi in this.keyEls) {
      const k = this.keyEls[midi];
      const on = this.active.has(+midi);
      k.sw.style.background = on ? k.color : (k.black ? '#23232e' : 'transparent');
      k.el.style.background = on ? (k.black ? '#1a1a24' : '#c8c8d8') : '';
      k.el.style.boxShadow = on ? `0 0 12px ${k.color}` : '';
    }
  }

  private setupPointerEvents() {
    addEventListener('pointerup', () => { this.mouseDown = false; });
  }

  private setupKeyboard() {
    const KMAP: Record<string, number> = {
      a: 60, w: 61, s: 62, e: 63, d: 64, f: 65, t: 66, g: 67,
      y: 68, h: 69, u: 70, j: 71, k: 72
    };

    addEventListener('keydown', e => {
      if (e.repeat) return;
      const m = KMAP[e.key.toLowerCase()];
      if (m) this.onNoteOn(m);
    });

    addEventListener('keyup', e => {
      const m = KMAP[e.key.toLowerCase()];
      if (m) this.onNoteOff(m);
    });
  }

  private setupMIDI() {
    if (navigator.requestMIDIAccess) {
      navigator.requestMIDIAccess().then(acc => {
        for (const inp of acc.inputs.values()) {
          inp.onmidimessage = msg => {
            if (!msg.data) return;
            const st = msg.data[0];
            const nt = msg.data[1];
            const ve = msg.data[2];
            const cmd = st & 0xf0;
            if (cmd === 0x90 && ve > 0) this.onNoteOn(nt, ve / 127);
            else if (cmd === 0x80 || (cmd === 0x90 && ve === 0)) this.onNoteOff(nt);
          };
        }
      }).catch(() => {});
    }
  }
}